import React, { useCallback, useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import clsx from 'classnames';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  Button,
  Drawer,
  Grid,
  Hidden,
  IconButton,
  Typography,
} from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';

import { loginRedirect } from 'utils';
import { AuthCtx } from 'components/auth/auth-ctx';
import { LanguageChooser } from 'components/i18n/language-chooser';
import { CartHeader } from 'components/order-ctx/cart-header';

import { MenuItem, useMenuItems } from './items';
import { AvatarInfo } from './avatar-info';
import { Notifications } from 'components/user-notifications/notifications';
import { I18nCtx } from 'components/i18n/i18n-context';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      boxShadow: theme.shadows[1],
      backgroundColor: 'white',
      position: 'fixed',
      top: 0,
      zIndex: 1000,
    },
    logoWrapper: {
      width: '100%',
      height: 80,
      display: 'flex',
    },
    menuWrapper: {
      width: '100%',
      height: 80,
      display: 'flex',
      justifyContent: 'flex-end',
    },
    buttonsWrapper: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',

      '& > *': {
        '@media(min-width: 1280px)': {
          margin: `${theme.spacing(2)}px 5px`,
        },
        '@media(min-width: 1400px)': {
          margin: `${theme.spacing(2)}px 10px`,
        },
      },
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(2),
      cursor: 'pointer',
    },
    text: {
      textTransform: 'initial',
      textDecoration: 'none',
    },
    defaultText: {
      fontWeight: 600,
      fontSize: 14,
      color: 'black',
      textTransform: 'initial',
    },
    primaryText: {
      fontWeight: 'bold',
      color: 'white',
      textTransform: 'initial',
    },
    redText: {
      fontWeight: 'bold',
      color: theme.palette.primary.dark,
      textTransform: 'initial',
    },
    primaryListItem: {
      backgroundColor: theme.palette.primary.main,

      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    logo: {
      maxWidth: '100%',
      width: 'auto',
      height: 'auto',
      maxHeight: 65,
    },
    menuIcon: {
      cursor: 'pointer',
    },
    drawer: {},
    drawerHeader: {},
    list: {
      width: 300,
    },
  })
);

export function Header() {
  const classes = useStyles();

  /**
   *
   */
  const [openDrawer, setOpenDrawer] = useState(false);

  const history = useHistory();

  const { userInfo, userSheet } = useContext(AuthCtx);
  const { locale } = useContext(I18nCtx);

  /**
   *
   */
  const handleNavigate = useCallback(
    (href: string) => {
      setOpenDrawer(false);

      history.push(href);
    },
    [history]
  );

  const menuItems = useMenuItems(userSheet);

  /**
   * Filter out items hiden for (un)logged user.
   */
  let items: MenuItem[] = [];
  if (userInfo) {
    items = menuItems.filter((item) => item.showToLogged);
  } else {
    items = menuItems.filter((item) => item.showToUnlogged);
  }

  return (
    <Grid container className={classes.root}>
      <Grid item md={2} xs={6}>
        <Typography component="nav" className={classes.logoWrapper}>
          <div className={classes.link} onClick={() => history.push('/')}>
            <img
              src={`${process.env.PUBLIC_URL}/archiv.png`}
              alt="logo"
              className={classes.logo}
            />
          </div>
        </Typography>
      </Grid>
      <Grid
        item
        md={10}
        xs={6}
        onClick={() => {
          // const local = `http://localhost:3000${window.location.pathname}`;
          // console.log('coppied: ', local);
          // navigator.clipboard.writeText(local);
        }}
      >
        <div className={classes.menuWrapper}>
          <div className={classes.buttonsWrapper}>
            <Hidden mdDown>
              <Button
                component="a"
                href={
                  locale === 'en'
                    ? 'https://udauk.cuni.cz/ARCHEN-1.html'
                    : 'https://udauk.cuni.cz/ARCH-1.html'
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.defaultText}
                >
                  Web ÚDAUK
                </Typography>
              </Button>
              {items.map((item) => (
                <Link key={item.label + item.href} to={item.href}>
                  <Button
                    component="span"
                    variant={item.primary ? 'contained' : 'text'}
                    color={item.primary ? 'primary' : 'default'}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      className={clsx({
                        [classes.primaryText]: item.primary,
                        [classes.defaultText]: !item.primary,
                      })}
                    >
                      {item.label}
                    </Typography>
                  </Button>
                </Link>
              ))}
              {userInfo ? (
                <AvatarInfo userInfo={userInfo} userSheet={userSheet} />
              ) : (
                <Button
                  component="span"
                  variant={'contained'}
                  color={'primary'}
                  onClick={() => loginRedirect()}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    className={clsx({
                      [classes.primaryText]: true,
                    })}
                  >
                    Přihlásit se
                  </Typography>
                </Button>
              )}
              {userInfo && <Notifications />}
              <CartHeader />
              <LanguageChooser />
            </Hidden>
            <Hidden lgUp>
              <IconButton onClick={() => setOpenDrawer(true)}>
                <MenuIcon />
              </IconButton>

              <Drawer
                className={classes.drawer}
                variant="temporary"
                anchor="right"
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
              >
                <div className={classes.drawerHeader}>
                  <IconButton onClick={() => setOpenDrawer(false)}>
                    <ChevronRightIcon />
                  </IconButton>
                  <LanguageChooser />
                </div>
                <List component="nav" dense={true} className={classes.list}>
                  {items.map((item) => (
                    <ListItem
                      button
                      key={item.label}
                      className={clsx({
                        [classes.primaryListItem]: item.primary,
                      })}
                      onClick={() => handleNavigate(item.href)}
                    >
                      <Link to={item.href}>
                        <ListItemText
                          primary={item.label}
                          className={clsx({
                            [classes.primaryText]: item.primary,
                            [classes.defaultText]: !item.primary,
                          })}
                        />
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </Drawer>
            </Hidden>
          </div>
        </div>
      </Grid>
    </Grid>
  );
}
